import axios, { AxiosInstance } from 'axios';
import { auth } from './firebase';
import {
  Category,
  Book,
  Chapter,
  Section,
} from '@/types';

const api: AxiosInstance = axios.create({
  baseURL: '/api',
});

// Attach Firebase auth token to all requests
api.interceptors.request.use(async (config) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
});

export const apiClient = {
  // Categories
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/books/categories');
    return response.data?.data?.categories ?? [];
  },

  // Books
  getBooks: async (category?: string): Promise<Book[]> => {
    const params = category ? { category } : {};
    const response = await api.get('/books', { params });
    return response.data?.data?.books ?? [];
  },

  getBook: async (bookId: string): Promise<Book> => {
    const response = await api.get(`/books/${bookId}`);
    return response.data?.data?.book;
  },

  // Chapters
  getChapters: async (bookId: string): Promise<Chapter[]> => {
    const response = await api.get(`/books/${bookId}/chapters`);
    return response.data?.data?.chapters ?? [];
  },

  getChapter: async (bookId: string, chapterId: string): Promise<Chapter> => {
    const response = await api.get(
      `/books/${bookId}/chapters/${chapterId}`
    );
    return response.data?.data?.chapter;
  },

  // Sections
  getSections: async (
    bookId: string,
    chapterId: string
  ): Promise<Section[]> => {
    const response = await api.get(
      `/books/${bookId}/chapters/${chapterId}/sections`
    );
    return response.data?.data?.sections ?? [];
  },

  // Audio URL
  getAudioUrl: async (audioFileId: string): Promise<string> => {
    if (!audioFileId) {
      return '';
    }
    const response = await api.get(
      `/audio/signed-url/${audioFileId}`
    );
    return response.data?.data?.signed_url;
  },
};

export default api;
